// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { REORDER_PRODUCTS_ID } from "@adl/App/Widgets/ReorderPad/ReorderPad";
import ProductListProductGridCard from "@adl/Overrides/Widgets/ProductList/ProductListProductGridCard";
import { color, xScreen } from "@adl/XDL/Utilities";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import CardContainerMultiColumn, {
    CardContainerMultiColumnStyles,
} from "@insite/content-library/Components/CardContainerMultiColumn";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import { ProductCarouselProductStyles } from "@insite/content-library/Components/ProductCarouselProduct";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    products: ProductModel[];
}

type Props = OwnProps;

interface ReorderPadGridStyles {
    container?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    cardContainerMultiColumnStyles?: CardContainerMultiColumnStyles;
    cardList?: CardListStyles;
}

const reorderPadGridStyles: ReorderPadGridStyles = {
    container: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 20px;

            ${xScreen("lte", "xs")} {
                justify-content: center;
            }
        `,
    },
    cardContainerMultiColumnStyles: {
        gridItem: {
            css: css``,
        },
        cardDivider: {
            css: css`
                height: 100%;
                width: 100%;
            `,
        },
    },
    cardList: {
        gridContainer: {
            gap: 0,
            css: css`
                margin-bottom: 5px;
                &[data-test-selector="reorderPad-productListCards"] {
                    margin-bottom: 12px;
                    padding-bottom: 22px;
                    border-bottom: 1px solid ${color("common.border")};
                }
            `,
        },
    },
};

const styles = reorderPadGridStyles;

interface ReorderPadGridItemProps {
    product: ProductModel;
}

const mapStateToProps = (state: ApplicationState, ownProps: ReorderPadGridItemProps) => ({
    productInfo: getProductInfoFromList(state, REORDER_PRODUCTS_ID, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
};

const ReorderPadGridItem: FC<
    ReorderPadGridItemProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>
> = ({ product, productInfo, updateProductInfo }) => {
    if (!product || !productInfo) {
        return null;
    }

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: REORDER_PRODUCTS_ID, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: REORDER_PRODUCTS_ID, productId: product.id, unitOfMeasure });
    };

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    return (
        <ProductContext.Provider value={productContext}>
            <CardContainerMultiColumn extendedStyles={styles.cardContainerMultiColumnStyles}>
                <ProductListProductGridCard
                    showCompare={true}
                    showTitle={true}
                    showAddToList={true}
                    showBrand={true}
                    showPrice={true}
                    showPartNumbers={true}
                    showAvailability={true}
                    showImage={true}
                    showAttributes={false}
                    showReorderPadProductAttributes={true}
                />
            </CardContainerMultiColumn>
        </ProductContext.Provider>
    );
};

const ProductGridItem = connect(mapStateToProps, mapDispatchToProps)(ReorderPadGridItem);

const ReorderPadGrid: FC<Props> = ({ products }) => {
    return (
        <CardList extendedStyles={styles.cardList} data-test-selector="reorderPad-productListCards">
            {products.map((product, idx) => (
                <ProductGridItem product={product} key={product.id} />
            ))}
        </CardList>
    );
};

export default ReorderPadGrid;
